import React from 'react'
import { v1 as uuid } from 'uuid'

import { createNotification } from '@/app/module/notifications/store/actions'
import { Verto, VertoCall } from '@/app/module/utils/verto'
import { useOnline } from '@/app/service/util/online'
import { useAppDispatch } from '@/app/store/redux-hooks'
import config from '@/config'

import CallDialogUI, { CallerIdType, CallStateType } from './call-dialog-ui'

const callErrorMessages: Record<string, string> = {
  'auth.notoken': 'Authentication failed. Please refresh the page and try again.',
  'carrier.nocarrier': 'This phone number cannot be called. Please contact support.',
  'contact.notfound': 'Contact not found.',
  'funds.dailylimit': 'Daily spending limit reached.',
  'funds.insufficient': 'Insufficient account balance. Please add funds.',
  'org.notfound': 'Organization not found.',
  'phonenumber.blocked': 'This phone number has been blocked by the organization.',
}

const getCallErrorMessage = (error: { message?: string; data?: { errorCode?: string } }): string => {
  const errorCode = error?.data?.errorCode
  if (errorCode && callErrorMessages[errorCode]) {
    return callErrorMessages[errorCode]
  }
  return 'Something went wrong. Please try again and contact support, if the problem persists.'
}

type Props = {
  autoStart?: boolean
  callerIds: CallerIdType[]
  contactId: string
  contactName: string
  orgId: number
  subId?: number
  token: string

  onClose: () => void
}

const socketUrl = config.webRTC.url || ''
const login = config.webRTC.username || ''
const passwd = config.webRTC.password || ''

const systemDefaultCallerId: CallerIdType = {
  label: 'System Default',
  value: '',
}

export default function CallDialog(props: Props) {
  const { autoStart = false, token, orgId, callerIds = [], onClose, contactId, contactName, subId } = props
  const dispatch = useAppDispatch()

  const autoStartedContactIdRef = React.useRef<string | null>(null)
  const previousContactIdRef = React.useRef(contactId)
  const [call, setCall] = React.useState<VertoCall | undefined>(undefined)
  const [callState, setCallState] = React.useState<CallStateType>(CallStateType.Default)
  const [callerId, setCallerId] = React.useState<CallerIdType>(systemDefaultCallerId)
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined)
  const [loading, setLoading] = React.useState(false)
  const [stream, setStream] = React.useState<MediaStream | undefined>(undefined)
  const [verto, setVerto] = React.useState<Verto | undefined>(undefined)
  const [vertoState, setVertoState] = React.useState<'connecting' | 'connected'>('connecting')
  const [isRecording, setIsRecording] = React.useState(false)

  React.useEffect(() => {
    if (previousContactIdRef.current !== contactId) {
      previousContactIdRef.current = contactId
      setErrorMessage(undefined)
    }
  }, [contactId])

  React.useEffect(() => {
    if (!autoStart) {
      autoStartedContactIdRef.current = null
    }
  }, [autoStart])

  React.useEffect(() => {
    let v: Verto | undefined

    const connect = async (vertoInstance: Verto): Promise<void> => {
      setVertoState('connecting')
      try {
        await vertoInstance.login()
        setVertoState('connected')
        return Promise.resolve()
      } catch {
        setVertoState('connecting')
        return connect(vertoInstance) // retry until login success
      }
    }

    const loadVerto = async () => {
      v = new Verto({
        rtcConfig: {
          iceServers: [
            {
              urls: ['stun:stun.freeswitch.org:3478', 'stun:stun.bethesda.com:3478'],
            },
          ],
        },
        transportConfig: {
          login,
          passwd,
          socketUrl,
        },
        ice_timeout: 5000,
      })
      await connect(v)
      setVerto(v)
      setCallState(CallStateType.Idle)
    }

    loadVerto()

    return () => {
      if (v) {
        v.logout()
      }
    }
  }, [])

  const closeTracks = React.useCallback((s?: MediaStream) => {
    if (!s) {
      return
    }
    s.getTracks().forEach((track) => {
      track.stop()
    })
    setStream(undefined)
  }, [])

  React.useEffect(() => {
    if (call) {
      call.subscribeEvent('initialized', () => {
        setCallState(CallStateType.Initialized)
      })

      call.subscribeEvent('answer', () => {
        setCallState(CallStateType.Ringing)
      })

      call.subscribeEvent('display', () => {
        setCallState(CallStateType.Incall)
      })

      call.subscribeEvent('hangup', () => {
        setCallState(CallStateType.HangingUp)
      })

      call.subscribeEvent('bye', () => {
        call.unsubscribeEvent('initialized')
        call.unsubscribeEvent('answer')
        call.unsubscribeEvent('display')
        call.unsubscribeEvent('hangup')
        call.unsubscribeEvent('bye')
        call.unsubscribeEvent('error')
        call.unsubscribeEvent('track')
        setCall(undefined)
        closeTracks(stream)
        setIsRecording(false)

        setCallState(CallStateType.CallEnded)
      })

      call.subscribeEvent('error', (error) => {
        setErrorMessage(getCallErrorMessage(error))
      })

      call.subscribeEvent('track', (track) => {
        if (track.kind !== 'audio') return
        const localStream = new MediaStream()
        localStream.addTrack(track)
        const el = document.getElementById('audio') as HTMLVideoElement
        el.srcObject = localStream
      })
    }
  }, [call, stream, closeTracks])

  const isUnmounting = React.useRef(false)
  React.useEffect(
    () => () => {
      isUnmounting.current = true
    },
    [],
  )

  React.useEffect(
    () => () => {
      // if there's a call when unmounting, clear the call event listener and set call to undefined
      if (call && isUnmounting.current) {
        call.unsubscribeEvent('initialized')
        call.unsubscribeEvent('answer')
        call.unsubscribeEvent('display')
        call.unsubscribeEvent('hangup')
        call.unsubscribeEvent('bye')
        call.unsubscribeEvent('error')
        call.unsubscribeEvent('track')
        setCall(undefined)
        setCallState(CallStateType.Idle)
        setIsRecording(false)
      }
    },
    [call],
  )

  React.useEffect(() => {
    if (callState === CallStateType.CallEnded) {
      setTimeout(() => {
        setCallState(CallStateType.Idle)
      }, 1000)
    }
  }, [callState])

  const online = useOnline()
  const callContact = React.useCallback(async () => {
    if (loading || !verto) {
      return
    }
    if (!online) {
      dispatch(
        createNotification({
          offline: {
            type: 'error',
            message: 'You are offline. Please check your internet connection before making a call.',
          },
        }),
      )
      return
    }
    setErrorMessage(undefined)
    setLoading(true)
    try {
      const localStream = await navigator.mediaDevices.getUserMedia({ audio: true })
      setStream(localStream)
      const callOptions = {
        callID: uuid(),
        userVariables: {
          esOrgID: `${orgId}`,
          esTokenAPI: '',
          esTokenJWT: token,
          esSubID: subId && `${subId}`,
        },
      }
      const tempCall = verto.call(localStream.getTracks(), `escontact:${contactId}`, {
        ...callOptions,
        caller_id_name: callerId?.label,
        caller_id_number: callerId?.value,
      })
      setCall(tempCall)
      setCallState(CallStateType.Ringing)
    } finally {
      setLoading(false)
    }
  }, [callerId, contactId, dispatch, loading, online, orgId, subId, token, verto])

  const hangup = () => {
    if (!call) {
      return
    }

    call.hangup()
    closeTracks(stream)
  }

  const handleCallerIdChange = (newCallerId: CallerIdType) => {
    setCallerId(newCallerId)
  }

  React.useEffect(() => () => closeTracks(stream), [stream, closeTracks])

  const toggleRecording = () => {
    if (!call) {
      return
    }

    if (isRecording) {
      call.stopRecording()
    } else {
      call.startRecording({ transcribe: true, transcribeLang: '' })
    }
    setIsRecording(!isRecording)
  }

  const sendDtmf = (digit: string) => {
    if (!call || callState !== CallStateType.Incall || digit.length !== 1 || !'0123456789*#'.includes(digit)) {
      return
    }

    call.dtmf(digit)
  }

  const canCall = Boolean(verto) && !call && !loading && callState === CallStateType.Idle
  const canEnd = !!call && [CallStateType.Ringing, CallStateType.Initialized, CallStateType.Incall].includes(callState)
  const canRecord = !!call && callState === CallStateType.Incall
  const canSendDtmf = !!call && callState === CallStateType.Incall

  React.useEffect(() => {
    if (!autoStart || autoStartedContactIdRef.current === contactId || !canCall) {
      return
    }

    autoStartedContactIdRef.current = contactId
    callContact()
  }, [autoStart, callContact, canCall, contactId])

  return (
    <CallDialogUI
      callerIds={callerIds}
      callState={callState}
      canCall={canCall}
      canEnd={canEnd}
      canRecord={canRecord}
      canSendDtmf={canSendDtmf}
      connectionState={vertoState}
      contactName={contactName}
      errorMessage={errorMessage}
      recording={isRecording}
      selectedCallerId={callerId}
      onCall={callContact}
      onCallerIdChange={handleCallerIdChange}
      onClose={() => {
        setErrorMessage(undefined)
        onClose()
      }}
      onDismissError={() => setErrorMessage(undefined)}
      onHangup={hangup}
      onRecordToggle={toggleRecording}
      onSendDtmf={sendDtmf}
    />
  )
}
